import { useEffect, useState } from 'react'

//: Cada cuánto se pregunta si hay una versión nueva mientras la pestaña
//: sigue abierta y ociosa — el propio navegador revisa un Service Worker
//: nuevo mucho más espaciado (cada navegación o ~1/día, y este proyecto ni
//: siquiera tiene uno); sin este polling, alguien que deja deltabot
//: abierto viendo bots en vivo por horas nunca se entera solo de un deploy
//: nuevo (docs/PROMPT-CACHE-BUSTING.md).
const CHECK_INTERVAL_MS = 15 * 60_000

//: true en cuanto `/version.json` trae un `buildId` distinto al que cargó
//: esta pestaña (`__BUILD_ID__`, inyectado en build time — ver
//: vite.config.ts). Qué hacer con eso es decisión de quien lo usa — ver
//: components/UpdateBanner.tsx: acá es sólo la detección.
export function useVersionCheck(intervalMs: number = CHECK_INTERVAL_MS): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false)

  useEffect(() => {
    // Ya hay una detectada — no tiene sentido seguir preguntando hasta que
    // el usuario actualice (recarga la página entera).
    if (updateAvailable) return

    let cancelled = false

    async function check() {
      try {
        // `cache: 'no-store'` en el fetch mismo, no sólo confiar en el
        // header del servidor — cinturón y tirantes contra cualquier
        // caché intermedia que el `Cache-Control` de nginx no alcance a
        // cubrir (ver deploy/servidor/*.nginx.conf).
        const response = await fetch('/version.json', { cache: 'no-store' })
        if (!response.ok) return
        const data: { buildId?: string } = await response.json()
        if (!cancelled && data.buildId && data.buildId !== __BUILD_ID__) {
          setUpdateAvailable(true)
        }
      } catch {
        // Sin red, o el servidor todavía sirve un build viejo sin este
        // archivo — no hay nada que avisarle al usuario por esto.
      }
    }

    void check()
    const id = window.setInterval(check, intervalMs)

    // Además del timer: al volver a la pestaña (otra app, otra ventana,
    // minimizado) — es justo el momento en que alguien vuelve a mirar
    // después de deployar, y sin esto tenía que esperar hasta el próximo
    // tick del intervalo aunque el deploy ya estuviera arriba.
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') void check()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      cancelled = true
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [intervalMs, updateAvailable])

  return updateAvailable
}
